import { DateFormatter } from '@components'

import { amountToDisplayValue, Checkbox, InternalAccessor, TableColumn, Text } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { CellProps } from 'react-table'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ReviewEntriesColumnHeader } from '../elements/ReviewEntriesColumnHeader'
import { ReviewEntriesSelectCell } from '../elements/ReviewEntriesSelectCell'
import { ReviewEntriesStatusCell } from '../elements/ReviewEntriesStatusCell/ReviewEntriesStatusCell'
import { ReviewEntriesSupplierCell } from '../elements/ReviewEntriesSupplierCell'
import type { ReviewEntryTableRow } from '../types/reviewEntryTableRow'
import { isReviewEntryProcessingStatus } from '../utils/isReviewEntryProcessingStatus'

const DEFAULT_NULL_REPLACEMENT_VALUE = '-'

/*
 * Every column needs a `minWidth`: `ReviewEntriesTableWrapper` makes even `fixedSize` columns
 * shrinkable, so a column without one collapses to nothing instead of stopping at a readable
 * width. Together these floors keep the table usable down to a ~1060px viewport.
 */
const ACCOUNT_COLUMN_MIN_WIDTH = 76
const AMOUNT_COLUMN_MIN_WIDTH = 86
const DATE_COLUMN_MIN_WIDTH = 76
const DESCRIPTION_COLUMN_MIN_WIDTH = 80
const SELECTION_COLUMN_MIN_WIDTH = 20
const STATUS_COLUMN_MIN_WIDTH = 100
const SUPPLIER_COLUMN_MIN_WIDTH = 120
const VAT_RATE_COLUMN_MIN_WIDTH = 52

const getEmptyCellValue = (row: ReviewEntryTableRow): string | null =>
  row.isMultiLineHeader ? null : DEFAULT_NULL_REPLACEMENT_VALUE

const renderTruncatedTextCell = ({ data, row, value }: CellProps<ReviewEntryTableRow>): ReactElement | null => {
  if (value) {
    return <>{value}</>
  }

  const tableRow = data[row.id] as ReviewEntryTableRow
  const emptyValue = getEmptyCellValue(tableRow)

  return emptyValue ? <>{emptyValue}</> : null
}

export interface ReviewEntriesTableSelection {
  areAllEntriesSelected: boolean
  selectableEntryIds: string[]
  selectedEntryIds: string[]
  toggleAllEntries: () => void
  toggleEntry: (entryId: string) => void
}

interface UseReviewEntriesTableColumnsProps {
  selection?: ReviewEntriesTableSelection
}

export const useReviewEntriesTableColumns = ({
  selection,
}: UseReviewEntriesTableColumnsProps = {}): TableColumn<ReviewEntryTableRow>[] => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const currencyId = organization?.baseCurrencyId ?? 'EUR'

  return useMemo(() => {
    const getColumnHeader = (label: string, tooltip: string) => ({
      Header: <ReviewEntriesColumnHeader label={label} tooltip={tooltip} />,
      headerName: label,
      shouldWrapInText: false,
    })

    return [
      // the design-system Table hides a column with id `selection`, hence `entrySelection`.
      // only auto-validatable document header rows get a checkbox
      ...(selection
        ? ([
            {
              Cell: ({ row, data }: CellProps<ReviewEntryTableRow>) => {
                const tableRow = data[row.id] as ReviewEntryTableRow

                if (
                  !tableRow.showDocumentLevelFields ||
                  isReviewEntryProcessingStatus(tableRow.status) ||
                  !selection.selectableEntryIds.includes(tableRow.entryId)
                ) {
                  return null
                }

                return (
                  <ReviewEntriesSelectCell
                    checked={selection.selectedEntryIds.includes(tableRow.entryId)}
                    name={`select-entry-${tableRow.entryId}`}
                    onChange={() => selection.toggleEntry(tableRow.entryId)}
                  />
                )
              },
              fixedSize: true,
              Header: (
                <Checkbox
                  checked={selection.areAllEntriesSelected}
                  disabled={!selection.selectableEntryIds.length}
                  indeterminate={!selection.areAllEntriesSelected && selection.selectedEntryIds.length > 0}
                  name="select-all-entries"
                  onChange={selection.toggleAllEntries}
                />
              ),
              id: 'entrySelection',
              minWidth: SELECTION_COLUMN_MIN_WIDTH,
              renderAsChildren: true,
              shouldWrapInText: false,
              size: 'xxxs',
              skipOnRowClick: true,
            },
          ] as TableColumn<ReviewEntryTableRow>[])
        : []),
      {
        accessor: 'status',
        Cell: ({ row, data }) => {
          const tableRow = data[row.id] as ReviewEntryTableRow

          if (!tableRow.showDocumentLevelFields) {
            return null
          }

          return <ReviewEntriesStatusCell questions={tableRow.questions} status={tableRow.status} />
        },
        fixedSize: true,
        ...getColumnHeader(t('review_entries.table.column.status'), t('review_entries.table.column.status.tooltip')),
        minWidth: STATUS_COLUMN_MIN_WIDTH,
        renderAsChildren: true,
        size: 'm',
        truncate: true,
      },
      {
        accessor: 'date',
        Cell: ({ row, data }) => {
          const tableRow = data[row.id] as ReviewEntryTableRow

          if (!tableRow.showDocumentLevelFields || !tableRow.date) {
            return null
          }

          return <DateFormatter value={tableRow.date} />
        },
        fixedSize: true,
        ...getColumnHeader(t('review_entries.table.column.date'), t('review_entries.table.column.date.tooltip')),
        minWidth: DATE_COLUMN_MIN_WIDTH,
        renderAsChildren: true,
        size: 's',
        sortable: true,
      },
      {
        accessor: 'supplierName',
        Cell: ({ row, data }) => {
          const tableRow = data[row.id] as ReviewEntryTableRow

          if (!tableRow.showDocumentLevelFields) {
            return null
          }

          return <ReviewEntriesSupplierCell questions={tableRow.questions} supplierName={tableRow.supplierName} />
        },
        ...getColumnHeader(
          t('review_entries.table.column.supplier'),
          t('review_entries.table.column.supplier.tooltip'),
        ),
        minWidth: SUPPLIER_COLUMN_MIN_WIDTH,
        size: 'm',
        sortable: true,
        truncate: true,
      },
      {
        accessor: 'description',
        Cell: renderTruncatedTextCell,
        ...getColumnHeader(
          t('review_entries.table.column.description'),
          t('review_entries.table.column.description.tooltip'),
        ),
        minWidth: DESCRIPTION_COLUMN_MIN_WIDTH,
        size: 'l',
        truncate: true,
      },
      {
        accessor: 'account',
        Cell: renderTruncatedTextCell,
        fixedSize: true,
        ...getColumnHeader(t('review_entries.table.column.account'), t('review_entries.table.column.account.tooltip')),
        minWidth: ACCOUNT_COLUMN_MIN_WIDTH,
        size: 's',
        sortable: true,
        truncate: true,
      },
      {
        accessor: 'amountExclVat',
        alignment: 'right',
        Cell: ({ row, data }) => {
          const tableRow = data[row.id] as ReviewEntryTableRow

          if (tableRow.amountExclVat === undefined) {
            return getEmptyCellValue(tableRow)
          }

          return <>{amountToDisplayValue(tableRow.amountExclVat)}</>
        },
        fixedSize: true,
        ...getColumnHeader(
          `${t('review_entries.table.column.excl_vat')} (${currencyId})`,
          t('review_entries.table.column.excl_vat.tooltip'),
        ),
        minWidth: AMOUNT_COLUMN_MIN_WIDTH,
        renderAsChildren: true,
        size: 'm',
        sortable: true,
      },
      {
        accessor: 'vatRate',
        alignment: 'right',
        Cell: ({ row, data }) => {
          const tableRow = data[row.id] as ReviewEntryTableRow

          if (!tableRow.vatRate) {
            return getEmptyCellValue(tableRow)
          }

          return <>{tableRow.vatRate}</>
        },
        fixedSize: true,
        ...getColumnHeader(
          t('review_entries.table.column.vat_rate'),
          t('review_entries.table.column.vat_rate.tooltip'),
        ),
        minWidth: VAT_RATE_COLUMN_MIN_WIDTH,
        renderAsChildren: true,
        size: 's',
        sortable: true,
      },
      {
        accessor: 'vatAmount',
        alignment: 'right',
        Cell: ({ row, data }) => {
          const tableRow = data[row.id] as ReviewEntryTableRow

          if (tableRow.vatAmount === undefined) {
            return getEmptyCellValue(tableRow)
          }

          return <>{amountToDisplayValue(tableRow.vatAmount)}</>
        },
        fixedSize: true,
        ...getColumnHeader(
          `${t('review_entries.table.column.vat')} (${currencyId})`,
          t('review_entries.table.column.vat.tooltip'),
        ),
        minWidth: AMOUNT_COLUMN_MIN_WIDTH,
        renderAsChildren: true,
        size: 's',
        sortable: true,
      },
      {
        accessor: 'amountInclVat',
        alignment: 'right',
        Cell: ({ row, data }) => {
          const tableRow = data[row.id] as ReviewEntryTableRow

          if (tableRow.amountInclVat === undefined) {
            return DEFAULT_NULL_REPLACEMENT_VALUE
          }

          return (
            <Text alignment="right" as="span" inherit weight="bold">
              {amountToDisplayValue(tableRow.amountInclVat)}
            </Text>
          )
        },
        fixedSize: true,
        ...getColumnHeader(
          `${t('review_entries.table.column.incl_vat')} (${currencyId})`,
          t('review_entries.table.column.incl_vat.tooltip'),
        ),
        minWidth: AMOUNT_COLUMN_MIN_WIDTH,
        renderAsChildren: true,
        size: 'm',
        sortable: true,
      },
      {
        accessor: InternalAccessor.Disabled,
      },
    ]
  }, [currencyId, selection, t])
}
